import { useState } from 'react';
import RequestDemoButton from '../components/RequestDemoButton/RequestDemoButton';
import { teamHeadshots } from '../config/teamAssets';
import { stockAssets } from '../config/stockAssets';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './AboutPage.module.css';

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  headshot: string;
  headshotPosition: string;
}

const team: TeamMember[] = [
  {
    name: 'Quintin Bohan',
    role: 'Co-Founder & Chief Executive Officer',
    headshot: teamHeadshots.quintinBohan,
    headshotPosition: '50% 30%',
    bio:
      'Quintin Bohan is a results-driven entrepreneur and operations professional with over a decade of experience in the service and hospitality industry, plus several years of logistical and inventory experience in aviation. Although not a member of the armed forces, he has worked extensively with the U.S. Navy, Coast Guard, and Army, developing specialized training programs and providing financial supervision. That collaboration gave him valuable insight into structured training systems, operational discipline, logistics, and the unique needs of both civilian and military-affiliated organizations — experience that informs his approach to building reliable, user-centric QR technology solutions as Co-Founder and CEO of ONI.',
  },
  {
    name: 'Ryan Crews',
    role: 'Co-Founder & Chief Technology Officer',
    headshot: teamHeadshots.ryanCrews,
    headshotPosition: '50% 18%',
    bio:
      'Technology executive with 10+ years leading the design and delivery of cloud-native platforms and mission-critical customer experiences. As Co-Founder and CTO of ONI, Ryan sets the technical vision and oversees the platform strategy that powers in-seat ordering at stadium scale — from edge infrastructure and real-time fulfillment to the APIs venue teams depend on when every second counts. He is focused on building reliable, high-performance systems that operators trust on game day and that fans experience without friction.',
  },
  {
    name: 'Natalie Williams',
    role: 'Co-Founder & Chief Growth Officer',
    headshot: teamHeadshots.natalieWilliams,
    headshotPosition: '50% 24%',
    bio:
      'Natalie brings more than a decade of experience in hospitality, event management, and venue operations to ONI, having partnered with some of the nation\'s leading venues, brands, and event organizations. Working firsthand alongside stadiums and arenas, she saw the challenges fans face navigating concessions during live events — insight that helped shape ONI\'s vision. At ONI, Natalie leads customer experience, strategic partnerships, and venue engagement, bridging the gap between innovative technology and exceptional hospitality.',
  },
];

function getInitials(name: string) {
  return name.split(' ').map((part) => part[0]).join('');
}

function TeamHeadshot({
  name,
  role,
  headshot,
  headshotPosition,
}: {
  name: string;
  role: string;
  headshot: string;
  headshotPosition: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !imageFailed;

  return (
    <div className={styles.headshot}>
      {showImage ? (
        <img
          src={headshot}
          alt={`${name}, ${role}`}
          className={styles.headshotImage}
          style={{ objectPosition: headshotPosition }}
          onError={() => setImageFailed(true)}
        />
      ) : null}
      {!showImage ? (
        <span className={styles.headshotPlaceholder} aria-hidden="true">
          {getInitials(name)}
        </span>
      ) : null}
    </div>
  );
}

function AboutPage() {
  usePageTitle('About');

  return (
    <>
      <section className={styles.hero}>
        <div className="container">
          <span className="sectionLabel">About ONI</span>
          <h1 className={styles.title}>Built for the game-day concession challenge</h1>
          <p className={styles.lead}>
            ONI was created to solve a problem every stadium operator knows: long concession lines, fans missing the action,
            and revenue left on the table. We connect in-seat ordering with the tools venue teams need to fulfill at scale.
          </p>
        </div>
      </section>

      <section className={styles.difference}>
        <div className="container">
          <img
            src={stockAssets.oniDifference}
            alt="The ONI Difference: fans stuck in concession lines before ONI versus ordering from their seats after ONI"
            className={styles.differenceImage}
          />
        </div>
      </section>

      <section className={`section ${styles.story}`}>
        <div className={`container ${styles.storyGrid}`}>
          <div>
            <h2 className="sectionTitle">Our story</h2>
            <p>
              ONI started with a simple question: Why should fans have to leave their seats and miss the action to get
              food or drinks?
            </p>
            <p>
              While attending an Atlanta United match at Mercedes-Benz Stadium, Quintin Bohan (CEO) found himself wanting
              another drink and snack but didn&apos;t want to miss the game by standing in a concession line. As fate would
              have it, his cousin, Natalie Williams (Chief Growth Officer), who worked on the hospitality side of the
              stadium, stopped by to say hello.
            </p>
            <p>
              The two began discussing the challenges stadiums face balancing fan satisfaction, operational efficiency,
              and revenue generation. Together, they recognized a massive opportunity: what if fans could order food,
              beverages, and merchandise directly to their seats without missing a moment of the action?
            </p>
            <p>
              Inspired by the idea, Quintin connected with his lifelong friend Ryan Crews (Chief Technology Officer), and
              the three began shaping the vision that would become ONI. Combining expertise in hospitality, customer
              experience, and technology, they set out to create a solution that benefits both fans and venues alike.
            </p>
            <p>
              Today, ONI helps stadiums, arenas, and entertainment venues increase revenue, reduce concession lines, and
              deliver a seamless guest experience from the first whistle to the final buzzer.
            </p>
            <p>
              Because fans shouldn&apos;t have to choose between enjoying the event and enjoying everything the venue has
              to offer.
            </p>
          </div>
          <div className={styles.missionCard}>
            <span className="sectionLabel">Mission</span>
            <p>Make stadium food service as seamless as the game itself.</p>
          </div>
        </div>
      </section>

      <section className={`section ${styles.team}`}>
        <div className="container">
          <span className="sectionLabel">Team</span>
          <h2 className="sectionTitle">The people behind ONI</h2>
          <p className="sectionSubtitle">
            Leadership team building in-seat ordering for stadiums and arenas.
          </p>
          <div className={styles.teamGrid}>
            {team.map((member) => (
              <article key={member.name} className={styles.teamCard}>
                <TeamHeadshot
                  name={member.name}
                  role={member.role}
                  headshot={member.headshot}
                  headshotPosition={member.headshotPosition}
                />
                <div className={styles.teamCardBody}>
                  <h3>{member.name}</h3>
                  <p className={styles.role}>{member.role}</p>
                  <p className={styles.bio}>{member.bio}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.bottomCta}>
        <div className={`container ${styles.bottomCtaInner}`}>
          <h2>Want to learn more?</h2>
          <p>Request a demo and we will show how ONI fits your venue&apos;s game-day operations.</p>
          <RequestDemoButton size="lg" />
        </div>
      </section>
    </>
  );
}

export default AboutPage;
